import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, Panel, LoadingState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { CandleChart } from "@/components/CandleChart";
import { CandlestickChart, Clock } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const TIMEFRAMES = ["1m", "5m", "15m", "60m", "75m", "125m", "240m", "1D", "1W", "1M"];
const PREP_TIMEFRAMES = ["1Q", "1H2", "1Y"]; // prepared engine, not available in Phase 1

export default function AdvancedCharts() {
  const [symbol, setSymbol] = useState("RELIANCE");
  const [tf, setTf] = useState("1D");
  const [showZones, setShowZones] = useState(true);
  const [showLevels, setShowLevels] = useState(true);

  const { data: instruments } = useQuery({
    queryKey: ["chart-instruments"],
    queryFn: async () => (await api.get("/instruments", { params: { page_size: 100 } })).data,
  });

  const { data: chart } = useQuery({
    queryKey: ["adv-candles", symbol, tf],
    queryFn: async () => (await api.get("/candles", { params: { symbol, timeframe: tf, limit: 180 } })).data,
  });

  return (
    <div>
      <PageHeader
        title="Advanced Charts"
        subtitle="Interactive candlesticks with GTF demand/supply zone overlays · DEMO data"
        icon={CandlestickChart}
        testId="advanced-charts-header"
      />

      <Panel testId="advanced-charts-panel">
        <div className="flex flex-wrap items-center gap-3 p-3 border-b border-[var(--border-subtle)]">
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger data-testid="chart-symbol-select" className="h-9 w-[200px] text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200 max-h-[300px]">
              {(instruments?.items || []).map((i) => (
                <SelectItem key={i.id} value={i.symbol}>{i.symbol} · {i.name.slice(0, 22)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex rounded-md border border-[var(--border-subtle)] overflow-hidden flex-wrap">
            {TIMEFRAMES.map((t) => (
              <button
                key={t}
                data-testid={`adv-tf-${t}`}
                onClick={() => setTf(t)}
                className={cn("px-3 py-1.5 text-[11px] font-mono-num transition-colors", tf === t ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-[var(--card-hover)]")}
              >
                {t}
              </button>
            ))}
            {PREP_TIMEFRAMES.map((t) => (
              <button
                key={t}
                data-testid={`adv-tf-${t}`}
                onClick={() => setTf(t)}
                title="Prepared engine — not available in Phase 1"
                className={cn("px-3 py-1.5 text-[11px] font-mono-num transition-colors", tf === t ? "bg-amber-600/70 text-white" : "text-slate-600 hover:bg-[var(--card-hover)]")}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center gap-1.5">
              <Switch data-testid="toggle-zones" checked={showZones} onCheckedChange={setShowZones} />
              <span className="text-[11px] text-slate-400">Zones</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Switch data-testid="toggle-levels" checked={showLevels} onCheckedChange={setShowLevels} />
              <span className="text-[11px] text-slate-400">Entry/SL/Target</span>
            </div>
            <DemoBadge />
          </div>
        </div>

        <div className="p-2">
          {!chart ? (
            <LoadingState label="Loading chart..." />
          ) : chart.supported === false ? (
            <div data-testid="tf-unsupported" className="flex flex-col items-center justify-center py-24 text-center">
              <Clock className="w-8 h-8 text-amber-400 mb-3" />
              <div className="text-sm text-slate-300 mb-1">Timeframe "{tf}" not available</div>
              <div className="text-xs text-slate-500 max-w-md">{chart.status}</div>
            </div>
          ) : (
            <>
              {chart.contract_status === "expired" && (
                <div className="mx-2 mb-2 rounded-md border border-rose-500/25 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-400" data-testid="expired-warning">
                  ⚠ Expired derivative contract — shown for reference only, not currently tradable.
                </div>
              )}
              <CandleChart
                key={`${symbol}-${tf}-${showZones}-${showLevels}`}
                candles={chart.candles}
                zones={chart.zones}
                levels={showLevels ? chart.levels : null}
                showZones={showZones}
                height={480}
              />
              <div className="flex items-center justify-between px-2 pt-2 text-[10px] text-slate-500 font-mono">
                <span>Last price {chart.last_price} = chart close {chart.candle_close}</span>
                <span>Zone source: synthetic · status: DEMO</span>
              </div>
            </>
          )}
        </div>
      </Panel>

      {chart && chart.levels && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {chart.zones.map((z, i) => (
            <Panel key={i} testId={`zone-card-${z.type}`}>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={cn("text-xs font-semibold uppercase tracking-wide", z.type === "demand" ? "text-emerald-400" : "text-rose-400")}>
                    {z.type} Zone · {z.pattern}
                  </span>
                  <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded border", z.fresh ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-slate-400 border-slate-600/40")}>
                    {z.fresh ? "Fresh" : "Tested"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Proximal</span><div className="font-mono-num text-slate-200">{fmtMoney(z.proximal)}</div></div>
                  <div><span className="text-slate-500">Distal</span><div className="font-mono-num text-slate-200">{fmtMoney(z.distal)}</div></div>
                  <div><span className="text-slate-500">Strength</span><div className="font-mono-num text-slate-200">{z.strength}</div></div>
                  <div><span className="text-slate-500">Source</span><div className="font-mono-num text-amber-400 uppercase">{z.source} · {z.status}</div></div>
                </div>
              </div>
            </Panel>
          ))}
          <Panel testId="zone-card-levels">
            <div className="p-4">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-400">Trade Levels</span>
              <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                <div><span className="text-slate-500">Entry</span><div className="font-mono-num text-blue-400">{fmtMoney(chart.levels.entry)}</div></div>
                <div><span className="text-slate-500">Stop</span><div className="font-mono-num text-rose-400">{fmtMoney(chart.levels.stop_loss)}</div></div>
                <div><span className="text-slate-500">Target</span><div className="font-mono-num text-emerald-400">{fmtMoney(chart.levels.target)}</div></div>
              </div>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
